/**
 * Authoring progress for a card (distinct from SRS “mastered”).
 *
 * - **completed** — study-frontmatter `version` is a number ≥ 0 (`version: 0` is the initial
 *   completed mark; any newer version also counts as completed). Runtime slices that only carry
 *   the derived DB flag may still pass `completed: true` from `completedAt !== null`.
 * - **needs-description** — no mnemonic text after trim.
 * - **needs-review** — has a mnemonic but is not marked completed.
 */
export type CardAuthoringDisplayState = "completed" | "needs-description" | "needs-review";

/** Initial study-frontmatter version written when a card is first marked completed. */
export const INITIAL_STUDY_VERSION = 0;

type CardAuthoringInput = {
  /**
   * Derived boolean from the content DB (`completedAt !== null`). Prefer `version` when reading
   * frontmatter; this field is for runtime slices that only carry the derived flag.
   */
  completed?: boolean | null;
  mnemonic?: string | null;
  /**
   * Study-frontmatter `version:`. Present and ≥ {@link INITIAL_STUDY_VERSION} means
   * authoring-complete.
   */
  version?: number | null;
};

/** True when a study-frontmatter `version` marks the card as authoring-complete. */
export function isStudyVersionCompleted(version: number | null | undefined): boolean {
  return typeof version === "number" && version >= INITIAL_STUDY_VERSION;
}

export function isCardCompleted(graphemeCard: {
  completed?: boolean | null;
  version?: number | null;
}): boolean {
  if (graphemeCard.version !== undefined && graphemeCard.version !== null) {
    return isStudyVersionCompleted(graphemeCard.version);
  }
  return graphemeCard.completed === true;
}

export function getCardAuthoringDisplayState(
  graphemeCard: CardAuthoringInput,
): CardAuthoringDisplayState {
  if (isCardCompleted(graphemeCard)) {
    return "completed";
  }
  if (!graphemeCard.mnemonic?.trim()) {
    return "needs-description";
  }
  return "needs-review";
}

export const cardAuthoringStateFillClassName: Record<CardAuthoringDisplayState, string> = {
  completed:
    "border-solid border-emerald-500 bg-emerald-100 dark:border-emerald-600 dark:bg-emerald-900/40",
  "needs-description":
    "border-solid border-amber-400/70 bg-zinc-200 dark:border-amber-800/60 dark:bg-zinc-800/55",
  "needs-review":
    "border-dashed border-emerald-500 bg-amber-100 dark:border-emerald-600 dark:bg-amber-950/45",
};

export function getCardAuthoringStateFillClassName(graphemeCard: CardAuthoringInput): string {
  return cardAuthoringStateFillClassName[getCardAuthoringDisplayState(graphemeCard)];
}
